import { useState, useCallback, useRef, useMemo } from 'react';
import {
  getFilterValues,
  setFilterValue,
  deleteFilterValue,
  clearFilterValues,
  flattenFilterValues,
} from '../../hooks/filterStore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FilterValue = string | string[] | boolean | Record<string, unknown> | null;

/**
 * Encapsulates dashboard filter state for the chart edit page.
 *
 * Shares the same `filterStore` as the Dashboard page so filter state
 * is preserved when navigating between dashboard view and chart edit.
 */
export function useChartFilters(dashboardId: string | undefined) {
  const [filterValues, setFilterValuesState] = useState<Map<string, FilterValue>>(
    () => (dashboardId ? getFilterValues(dashboardId) : new Map()),
  );

  /** Bumped when default filter values change so `resolvedFilters` recomputes. */
  const [defaultsVersion, setDefaultsVersion] = useState(0);

  /** Ref holding auto-applied default filter values (not "user-modified") */
  const defaultFilterValuesRef = useRef<Record<string, unknown>>({});

  /**
   * Optional setter for refreshTrigger — injected by the page so that
   * `handleFilterDefault` can bump the refresh counter without owning it.
   */
  const refreshTriggerRef = useRef<() => void>(() => {});

  /** Allow the parent to wire up a refresh callback */
  const setRefreshCallback = useCallback((cb: () => void) => {
    refreshTriggerRef.current = cb;
  }, []);

  const handleFilterChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (variable: string, value: any) => {
      if (!dashboardId) return;
      setFilterValuesState((prev) => {
        const next = new Map(prev);
        if ((Array.isArray(value) && value.length === 0) || value === null) {
          next.delete(variable);
          deleteFilterValue(dashboardId, variable);
        } else {
          next.set(variable, value);
          setFilterValue(dashboardId, variable, value);
        }
        return next;
      });
    },
    [dashboardId],
  );

  const handleFilterReset = useCallback(
    (variable: string) => {
      if (!dashboardId) return;
      setFilterValuesState((prev) => {
        if (!prev.has(variable)) return prev;
        const next = new Map(prev);
        next.delete(variable);
        deleteFilterValue(dashboardId, variable);
        return next;
      });
    },
    [dashboardId],
  );

  const handleResetAll = useCallback(() => {
    if (!dashboardId) return;
    setFilterValuesState(new Map());
    clearFilterValues(dashboardId);
  }, [dashboardId]);

  const handleFilterDefault = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (variable: string, value: any) => {
      defaultFilterValuesRef.current[variable] = value;
      // Bump version so resolvedFilters memo recomputes.
      // Do NOT bump refreshTrigger — defaults should use the cache, not force a refetch.
      setDefaultsVersion((v) => v + 1);
    },
    [],
  );

  // No-op readiness handler — chart edit doesn't gate on filter readiness
  const handleFilterReady = useCallback((_filterId: string) => {}, []);

  /**
   * Merged filters object: default values overlaid with user-modified values.
   * Single source of truth — use this everywhere instead of building inline.
   */
  const resolvedFilters = useMemo(() => {
    const obj: Record<string, unknown> = { ...defaultFilterValuesRef.current };
    for (const [k, v] of filterValues) {
      if (v != null) obj[k] = v;
    }
    // Range filters store nested objects under the variable key; the
    // chart-execution API contract is flat. See `flattenFilterValues`.
    return flattenFilterValues(obj);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultsVersion forces recompute when defaults change
  }, [filterValues, defaultsVersion]);

  return {
    filterValues,
    resolvedFilters,
    defaultFilterValuesRef,
    handleFilterChange,
    handleFilterReset,
    handleResetAll,
    handleFilterDefault,
    handleFilterReady,
    setRefreshCallback,
  };
}